/**
 * Pure model helpers for GroupedTreeTable: node shape, visible-row flattening,
 * group-id collection, and default column widths. Kept framework-free so they
 * can be unit tested directly.
 */
import type React from "react";

export interface TreeTableNode {
  /** Unique, stable id across the whole tree (e.g. the node's key path). */
  id: string;
  /** First-column content. */
  label: React.ReactNode;
  /** Plain-text name, used for accessible names ("Expand Oct 6"). Falls back
   *  to `label` when that is a string. */
  title?: string;
  /** Pre-formatted cell content keyed by column key. Missing → "—". */
  cells: Record<string, React.ReactNode>;
  children?: TreeTableNode[];
  /** Count-pill value for a group. Defaults to `children.length`. */
  count?: number;
  /** Render as a group row (chevron, count pill, group styling) even with no
   *  children — e.g. a day with no sales when grouped by staff, so it reads
   *  as the same level as its sibling days. Defaults to having children. */
  group?: boolean;
}

export interface TreeTableColumn {
  key: string;
  label: string;
  /** Right-aligned, tabular numbers. Defaults to true (metric columns). */
  numeric?: boolean;
  /** Minimum width in px. Defaults to an estimate that keeps the header
   *  label on one line. */
  minWidth?: number;
  /** Header tooltip body (e.g. a <MetricInfo />). */
  info?: React.ReactNode;
}

export interface VisibleTreeRow {
  node: TreeTableNode;
  depth: number;
  /** Rendered as a group row (see `isGroupNode`). */
  isGroup: boolean;
  open: boolean;
}

export const hasChildren = (node: TreeTableNode): boolean =>
  !!node.children && node.children.length > 0;

/** A group row: one with children, or one explicitly marked `group`. */
export const isGroupNode = (node: TreeTableNode): boolean =>
  node.group ?? hasChildren(node);

/** Depth-first list of the rows currently visible (collapsed groups hide
 *  their descendants). */
export function flattenVisibleRows(
  nodes: TreeTableNode[],
  collapsed: ReadonlySet<string>,
): VisibleTreeRow[] {
  const out: VisibleTreeRow[] = [];
  const walk = (list: TreeTableNode[], depth: number) => {
    for (const node of list) {
      const group = isGroupNode(node);
      const open = group && !collapsed.has(node.id);
      out.push({ node, depth, isGroup: group, open });
      if (open && hasChildren(node)) {
        walk(node.children as TreeTableNode[], depth + 1);
      }
    }
  };
  walk(nodes, 0);
  return out;
}

/** Every group row (see `isGroupNode`) as [id, node, depth]. */
export function collectGroups(
  nodes: TreeTableNode[],
): Array<{ id: string; node: TreeTableNode; depth: number }> {
  const out: Array<{ id: string; node: TreeTableNode; depth: number }> = [];
  const walk = (list: TreeTableNode[], depth: number) => {
    for (const node of list) {
      if (!isGroupNode(node)) continue;
      out.push({ id: node.id, node, depth });
      if (hasChildren(node)) walk(node.children as TreeTableNode[], depth + 1);
    }
  };
  walk(nodes, 0);
  return out;
}

/** True when any top-level node is a group row (i.e. the table is grouped). */
export const isGrouped = (nodes: TreeTableNode[]): boolean =>
  nodes.some(isGroupNode);

// Header labels are 12px / 600; ~6.9px per glyph plus cell padding and room
// for the sort arrow + info icon keeps every label on one line.
const HEADER_GLYPH_PX = 6.9;
const HEADER_CHROME_PX = 46;
const MIN_NUMERIC_PX = 112;
const MIN_TEXT_PX = 140;
const MIN_FIRST_PX = 200;
const FIRST_DEPTH_STEP_PX = 44;

export function estimateColumnWidth(label: string, numeric = true): number {
  const floor = numeric ? MIN_NUMERIC_PX : MIN_TEXT_PX;
  return Math.max(
    floor,
    Math.ceil(label.length * HEADER_GLYPH_PX + HEADER_CHROME_PX),
  );
}

/** First (label) column: wide enough for its header and the deepest indent. */
export function estimateFirstColumnWidth(
  label: string,
  maxDepth: number,
): number {
  return Math.max(
    MIN_FIRST_PX + maxDepth * FIRST_DEPTH_STEP_PX,
    Math.ceil(label.length * HEADER_GLYPH_PX + HEADER_CHROME_PX),
  );
}

export function maxTreeDepth(nodes: TreeTableNode[]): number {
  let max = 0;
  const walk = (list: TreeTableNode[], depth: number) => {
    for (const node of list) {
      if (depth > max) max = depth;
      if (hasChildren(node)) walk(node.children as TreeTableNode[], depth + 1);
    }
  };
  walk(nodes, 0);
  return max;
}
